import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, Calendar, Clock, Users, Video, ExternalLink, XIcon, EditIcon, Trash2Icon } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { coffeeData, Coffee } from '../components/coffee/coffeeData';
import AlgaCoffeeSummaryWidget from '../components/coffee/widgets/AlgaCoffeeSummaryWidget';
import AlgaCoffeeTranscriptWidget from '../components/coffee/widgets/AlgaCoffeeTranscriptWidget';
import AlgaCoffeeNotesWidget from '../components/coffee/widgets/AlgaCoffeeNotesWidget';
import EditCoffeeModal from '../components/coffee/EditCoffeeModal';
const CoffeeDetailPage = () => {
  const {
    coffeeId
  } = useParams<{
    coffeeId: string;
  }>();
  const navigate = useNavigate();
  const [coffee, setCoffee] = useState<Coffee | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'discussion' | 'participants'>('info');
  const [showEditModal, setShowEditModal] = useState(false);
  useEffect(() => {
    if (coffeeId) {
      const foundCoffee = coffeeData.find(c => c.id === coffeeId);
      if (foundCoffee) {
        setCoffee(foundCoffee);
      } else {
        // Coffee not found, redirect to coffee list
        navigate('/coffee');
      }
    }
  }, [coffeeId, navigate]);
  if (!coffee) {
    return <div className="flex h-screen bg-gray-100 items-center justify-center" data-id="element-2099">
        <p data-id="element-2100">Caricamento in corso...</p>
      </div>;
  }
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const isAdmin = coffee.type === 'admin';
  const now = new Date();
  const coffeeDate = new Date(coffee.date);
  const isPast = coffeeDate < now;
  const isJoined = coffee.participants.some(p => p.id === 'current-user');
  const hasParticipated = isPast && isJoined;
  const isHost = coffee.host.id === 'current-user';
  // Platform logos
  const getPlatformLogo = (platform: string) => {
    switch (platform) {
      case 'Google Meet':
        return 'https://uploadthingy.s3.us-west-1.amazonaws.com/hawwkzWf3TnSE8Q31Hrx5c/images.png';
      case 'Microsoft Teams':
        return 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c9/Microsoft_Office_Teams_%282018%E2%80%93present%29.svg/2203px-Microsoft_Office_Teams_%282018%E2%80%93present%29.svg.png';
      case 'Zoom':
        return 'https://uploadthingy.s3.us-west-1.amazonaws.com/a6M2ckhEkmcVgkXUVkifgg/zoom_logo_icon_147196.png';
      default:
        return null;
    }
  };
  const handleUpdateCoffee = (updatedCoffee: Partial<Coffee>) => {
    if (coffee) {
      // In a real app, this would make an API call
      // For now, we'll just update the local state
      setCoffee({
        ...coffee,
        ...updatedCoffee
      });
      // Show a success message
      alert('Evento aggiornato con successo!');
    }
  };
  const handleDeleteCoffee = () => {
    // In a real app, this would make an API call
    // For now, we'll just redirect to the coffee list
    navigate('/coffee');
    // Show a success message
    alert('Evento eliminato con successo!');
  };
  const renderContent = () => {
    switch (activeTab) {
      case 'info':
        return <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-2101">
            <div className="md:col-span-3" data-id="element-2102">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6" data-id="element-2103">
                <div className="p-6" data-id="element-2104">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-2105">
                    Informazioni sull'evento
                  </h2>
                  <p className="text-gray-600 mb-6" data-id="element-2106">{coffee.description}</p>
                  <h3 className="text-md font-medium text-gray-800 mb-2" data-id="element-2107">
                    Temi trattati
                  </h3>
                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-2108">
                    {coffee.topics.map((topic, index) => <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm" data-id="element-2109">
                        {topic}
                      </span>)}
                  </div>
                  {isPast ? hasParticipated ? <div className="bg-custom-green/10 rounded-lg p-4 text-center" data-id="element-2110">
                        <p className="text-sm text-custom-green font-medium" data-id="element-2111">
                          Hai partecipato a questo evento
                        </p>
                      </div> : <div className="bg-gray-100 rounded-lg p-4 text-center" data-id="element-2112">
                        <p className="text-sm text-gray-500" data-id="element-2113">
                          Evento terminato
                        </p>
                      </div> : <button className="w-full flex items-center justify-center py-3 bg-custom-green text-white rounded-lg hover:bg-custom-green/90 transition-colors font-medium" data-id="element-2114">
                      <ExternalLink size={18} className="mr-2" data-id="element-2115" />
                      Partecipa all'evento
                    </button>}
                </div>
              </div>
              {hasParticipated && <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-2116">
                  <AlgaCoffeeSummaryWidget coffeeTitle={coffee.title} coffeeHost={coffee.host.name} coffeeTopics={coffee.topics} data-id="element-2117" />
                  <AlgaCoffeeNotesWidget coffeeHost={coffee.host.name} data-id="element-2118" />
                </div>}
            </div>
            <div className="md:col-span-1" data-id="element-2119">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6" data-id="element-2120">
                <div className="p-4 border-b border-gray-200" data-id="element-2121">
                  <h2 className="text-base font-semibold text-gray-800" data-id="element-2122">
                    Dettagli dell'evento
                  </h2>
                </div>
                <div className="p-4" data-id="element-2123">
                  <div className="space-y-3" data-id="element-2124">
                    <div className="flex items-center text-gray-600" data-id="element-2125">
                      <Calendar size={18} className="mr-3 text-custom-green" data-id="element-2126" />
                      <span data-id="element-2127">{formatDate(coffee.date)}</span>
                    </div>
                    <div className="flex items-center text-gray-600" data-id="element-2128">
                      <Clock size={18} className="mr-3 text-custom-green" data-id="element-2129" />
                      <span data-id="element-2130">
                        {formatTime(coffee.date)} - {formatTime(coffee.endTime)}
                      </span>
                    </div>
                    <div className="flex items-center text-gray-600" data-id="element-2131">
                      {getPlatformLogo(coffee.platform) ? <img src={getPlatformLogo(coffee.platform)} alt={coffee.platform} className="w-5 h-5 mr-3" data-id="element-2132" /> : <Video size={18} className="mr-3 text-custom-green" data-id="element-2133" />}
                      <span data-id="element-2134">{coffee.platform}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6" data-id="element-2135">
                <div className="p-4 border-b border-gray-200" data-id="element-2136">
                  <h2 className="text-base font-semibold text-gray-800" data-id="element-2137">
                    Host
                  </h2>
                </div>
                <div className="p-4" data-id="element-2138">
                  <div className="flex items-center" data-id="element-2139">
                    <div className={`w-12 h-12 rounded-full overflow-hidden mr-3 flex items-center justify-center ${isAdmin ? 'bg-green-600' : 'bg-white'}`} data-id="element-2140">
                      {isAdmin ? <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback" className="w-7 h-7 object-contain brightness-0 invert" data-id="element-2141" /> : <img src={coffee.host.avatar} alt={coffee.host.name} className="w-full h-full object-cover" data-id="element-2142" />}
                    </div>
                    <div data-id="element-2143">
                      <p className="font-medium text-gray-800" data-id="element-2144">
                        {coffee.host.name}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-2145">
                        {isAdmin ? 'Greenback Team' : 'Community Member'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-2146">
                <div className="p-4 border-b border-gray-200" data-id="element-2147">
                  <h2 className="text-base font-semibold text-gray-800" data-id="element-2148">
                    Partecipanti
                  </h2>
                </div>
                <div className="p-4" data-id="element-2149">
                  <div className="flex items-center mb-3" data-id="element-2150">
                    <Users size={18} className="text-custom-green mr-2" data-id="element-2151" />
                    <span className="text-sm text-gray-500" data-id="element-2152">
                      {coffee.participants.length} partecipanti
                    </span>
                  </div>
                  <div className="space-y-3" data-id="element-2153">
                    {coffee.participants.map(participant => <div key={participant.id} className="flex items-center" data-id="element-2154">
                        <div className="w-8 h-8 rounded-full overflow-hidden mr-3" data-id="element-2155">
                          <img src={participant.avatar} alt={participant.name} className="w-full h-full object-cover" data-id="element-2156" />
                        </div>
                        <p className="text-sm text-gray-700" data-id="element-2157">
                          {participant.name}
                        </p>
                        {participant.id === 'current-user' && <span className="ml-auto text-xs bg-custom-green/10 text-custom-green px-2 py-0.5 rounded-full" data-id="element-2158">
                            Tu
                          </span>}
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>;
      case 'discussion':
        return <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2159">
            <div className="md:col-span-2" data-id="element-2160">
              {hasParticipated ? <AlgaCoffeeTranscriptWidget coffeeHost={coffee.host.name} coffeeTitle={coffee.title} data-id="element-2161" /> : <div className="bg-white rounded-xl border border-gray-200 p-6 text-center" data-id="element-2162">
                  <p className="text-gray-500" data-id="element-2163">
                    {isPast ? 'Non hai partecipato a questo evento, quindi la trascrizione non è disponibile.' : "La trascrizione sarà disponibile dopo l'evento."}
                  </p>
                </div>}
            </div>
            <div className="md:col-span-1" data-id="element-2164">
              {hasParticipated && <AlgaCoffeeNotesWidget coffeeHost={coffee.host.name} data-id="element-2165" />}
            </div>
          </div>;
      case 'participants':
        return <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2166">
            <div className="md:col-span-2" data-id="element-2167">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-2168">
                <div className="p-4 border-b border-gray-200" data-id="element-2169">
                  <h2 className="text-base font-semibold text-gray-800" data-id="element-2170">
                    Tutti i partecipanti
                  </h2>
                </div>
                <div className="p-6" data-id="element-2171">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2172">
                    {coffee.participants.map(participant => <div key={participant.id} className="flex items-center p-3 border border-gray-200 rounded-lg" data-id="element-2173">
                        <div className="w-10 h-10 rounded-full overflow-hidden mr-3" data-id="element-2174">
                          <img src={participant.avatar} alt={participant.name} className="w-full h-full object-cover" data-id="element-2175" />
                        </div>
                        <div data-id="element-2176">
                          <p className="font-medium text-gray-800" data-id="element-2177">
                            {participant.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-2178">
                            {participant.id === 'current-user' ? 'Tu' : 'Partecipante'}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
            <div className="md:col-span-1" data-id="element-2179">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-2180">
                <div className="p-4 border-b border-gray-200" data-id="element-2181">
                  <h2 className="text-base font-semibold text-gray-800" data-id="element-2182">
                    Informazioni sull'evento
                  </h2>
                </div>
                <div className="p-4" data-id="element-2183">
                  <div className="space-y-3" data-id="element-2184">
                    <div className="flex items-center text-gray-600" data-id="element-2185">
                      <Calendar size={18} className="mr-3 text-gray-500" data-id="element-2186" />
                      <span data-id="element-2187">{formatDate(coffee.date)}</span>
                    </div>
                    <div className="flex items-center text-gray-600" data-id="element-2188">
                      <Clock size={18} className="mr-3 text-gray-500" data-id="element-2189" />
                      <span data-id="element-2190">
                        {formatTime(coffee.date)} - {formatTime(coffee.endTime)}
                      </span>
                    </div>
                    <div className="flex items-center text-gray-600" data-id="element-2191">
                      {getPlatformLogo(coffee.platform) ? <img src={getPlatformLogo(coffee.platform)} alt={coffee.platform} className="w-5 h-5 mr-3" data-id="element-2192" /> : <Video size={18} className="mr-3 text-gray-500" data-id="element-2193" />}
                      <span data-id="element-2194">{coffee.platform}</span>
                    </div>
                    <div className="flex items-center text-gray-600" data-id="element-2195">
                      <Users size={18} className="mr-3 text-gray-500" data-id="element-2196" />
                      <span data-id="element-2197">{coffee.participants.length} partecipanti</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="flex h-screen bg-gray-100" data-id="element-2198">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 md:hidden ${sidebarOpen ? 'block' : 'hidden'}`} data-id="element-2199">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} data-id="element-2200"></div>
        <div className="relative flex-1 flex flex-col w-2/3 h-full bg-black" data-id="element-2201">
          <div className="absolute top-0 right-0 -mr-12 pt-2" data-id="element-2202">
            <button type="button" className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white" onClick={() => setSidebarOpen(false)} data-id="element-2203">
              <span className="sr-only" data-id="element-2204">Close sidebar</span>
              <XIcon className="h-6 w-6 text-white" aria-hidden="true" data-id="element-2205" />
            </button>
          </div>
          <div className="flex-1 h-full overflow-y-auto" data-id="element-2206">
            <Sidebar activePage="community" data-id="element-2207" />
          </div>
        </div>
      </div>
      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:flex-shrink-0" data-id="element-2208">
        <Sidebar activePage="community" data-id="element-2209" />
      </div>
      <div className="flex flex-col flex-1 overflow-hidden" data-id="element-2210">
        <Topbar onMenuClick={() => setSidebarOpen(true)} data-id="element-2211" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 pt-0 md:pt-0 bg-gray-100" data-id="element-2212">
          <div className="pt-4 md:pt-6 max-w-6xl mx-auto" data-id="element-2213">
            {/* Back button */}
            <div className="mb-6" data-id="element-2214">
              <button onClick={() => navigate('/coffee')} className="flex items-center text-gray-600 hover:text-gray-800" data-id="element-2215">
                <ArrowLeftIcon size={18} className="mr-2" data-id="element-2216" />
                <span data-id="element-2217">Torna agli Eventi</span>
              </button>
            </div>
            {/* Coffee Header */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6 overflow-hidden" data-id="element-2218">
              <div className="p-6" data-id="element-2219">
                <div className="flex flex-col sm:flex-row items-start gap-4" data-id="element-2220">
                  <div className={`w-full sm:w-[100px] h-[100px] rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center border-2 ${isAdmin ? 'bg-green-600 border-green-600' : 'bg-gray-100 border-gray-200'}`} data-id="element-2221">
                    {isAdmin ? <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback" className="w-12 h-12 object-contain brightness-0 invert" data-id="element-2222" /> : <img src={coffee.host.avatar} alt={coffee.host.name} className="w-full h-full object-cover" data-id="element-2223" />}
                  </div>
                  <div className="flex-1" data-id="element-2224">
                    <div className="flex justify-between items-start" data-id="element-2225">
                      <h1 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-2226">
                        {coffee.title}
                      </h1>
                      {isHost && !isPast && <div className="flex space-x-2" data-id="element-2227">
                          <button onClick={() => setShowEditModal(true)} className="flex items-center text-gray-600 hover:text-gray-800 px-2 py-1 rounded-md hover:bg-gray-100" data-id="element-2228">
                            <EditIcon size={16} className="mr-1" data-id="element-2229" />
                            <span className="text-sm" data-id="element-2230">Modifica</span>
                          </button>
                          <button onClick={() => {
                        if (window.confirm('Sei sicuro di voler eliminare questo evento?')) {
                          handleDeleteCoffee();
                        }
                      }} className="flex items-center text-red-600 hover:text-red-700 px-2 py-1 rounded-md hover:bg-red-50" data-id="element-2231">
                            <Trash2Icon size={16} className="mr-1" data-id="element-2232" />
                            <span className="text-sm" data-id="element-2233">Elimina</span>
                          </button>
                        </div>}
                    </div>
                    <p className="text-gray-600 mb-4 text-sm" data-id="element-2234">
                      Ospitato da {coffee.host.name} • {formatDate(coffee.date)}
                      {isHost && <span className="ml-2 text-blue-600 text-xs font-medium" data-id="element-2235">
                          (Sei l'host)
                        </span>}
                    </p>
                    <div className="flex flex-wrap items-center gap-4 sm:gap-6" data-id="element-2236">
                      <div className="flex items-center text-gray-500" data-id="element-2237">
                        <Clock size={16} className="mr-1.5" data-id="element-2238" />
                        <span className="text-sm" data-id="element-2239">
                          {formatTime(coffee.date)} -{' '}
                          {formatTime(coffee.endTime)}
                        </span>
                      </div>
                      <div className="flex items-center text-gray-500" data-id="element-2240">
                        <Users size={16} className="mr-1.5" data-id="element-2241" />
                        <span className="text-sm" data-id="element-2242">
                          {coffee.participants.length} partecipanti
                        </span>
                      </div>
                      <div className="flex items-center text-gray-500" data-id="element-2243">
                        {getPlatformLogo(coffee.platform) ? <img src={getPlatformLogo(coffee.platform)} alt={coffee.platform} className="w-4 h-4 mr-1.5" data-id="element-2244" /> : <Video size={16} className="mr-1.5" data-id="element-2245" />}
                        <span className="text-sm" data-id="element-2246">{coffee.platform}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Tab Navigation */}
              <div className="border-t border-gray-200 px-6 overflow-x-auto" data-id="element-2247">
                <nav className="flex space-x-8" data-id="element-2248">
                  <button onClick={() => setActiveTab('info')} className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'info' ? 'border-custom-green text-custom-green' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2249">
                    Informazioni
                  </button>
                  <button onClick={() => setActiveTab('discussion')} className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'discussion' ? 'border-custom-green text-custom-green' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2250">
                    Discussione
                  </button>
                  <button onClick={() => setActiveTab('participants')} className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'participants' ? 'border-custom-green text-custom-green' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2251">
                    Partecipanti
                  </button>
                </nav>
              </div>
            </div>
            {/* Content area */}
            {renderContent()}
          </div>
        </main>
      </div>
      {/* Edit Coffee Modal */}
      {showEditModal && coffee && <EditCoffeeModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} coffee={coffee} onUpdate={handleUpdateCoffee} onDelete={handleDeleteCoffee} data-id="element-2252" />}
    </div>;
};
export default CoffeeDetailPage;